/**
 * PeerCard — compact, read-only view of a teammate's stop (where & when only).
 * No tasks, notes, docs or action buttons.
 */
import { MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { JobTag, isContract, bandColor } from "@/components/dispatch/JobTag";
import { CrewLine } from "@/components/tech/CrewLine";
import { useI18n } from "@/i18n";
import { Tx } from "@/i18n/Tx";

const STATUS = {
  scheduled: ["Scheduled", "bg-blue-100 text-blue-700"], en_route: ["En Route", "bg-purple-100 text-purple-700"],
  en_route_shop: ["En Route · Shop", "bg-purple-100 text-purple-700"], at_shop: ["At Shop", "bg-indigo-100 text-indigo-700"],
  on_site: ["On Site", "bg-amber-100 text-amber-700"], done_for_day: ["Done", "bg-slate-200 text-slate-600"],
  completed: ["Completed", "bg-green-100 text-green-700"],
};

export default function PeerCard({ d }) {
  const { t } = useI18n();
  const st = d.dispatch_status || "scheduled";
  const [label, cls] = STATUS[st] || [st, "bg-slate-100 text-slate-600"];
  return (
    <div className="relative rounded-lg border border-slate-200 bg-white p-1.5 pl-2.5 text-[9px] md:text-[11px] mb-1 overflow-hidden" style={{ boxShadow: `inset 4px 0 0 ${bandColor(d)}` }}
      data-testid={`peer-dispatch-${d.id}`} data-kind={isContract(d) ? "contract" : "service"}>
      <div className="flex items-start gap-1 mb-0.5">
        <div className="font-semibold text-slate-800 text-[10px] md:text-xs leading-tight flex-1 min-w-0 line-clamp-2 break-words">
          {d.wo_title ? <Tx text={d.wo_title} /> : t("Untitled")}
          {!isContract(d) && <span className="text-blue-600 font-bold ml-1 text-[8px] whitespace-nowrap">{d.wo_number}</span>}
        </div>
        <JobTag d={d} testId={`peer-tag-${d.id}`} />
        <Badge className={`text-[7px] px-1 py-0 shrink-0 ${cls}`}>{t(label)}</Badge>
        <span className="text-[8px] text-slate-400 shrink-0">{d.hours}h</span>
      </div>
      <div className="text-slate-600 font-medium text-[9px] truncate">{d.client_name || "—"}</div>
      {d.site_address && <div className="text-slate-500 truncate text-[8px]"><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{d.site_address}</div>}
      <CrewLine crew={d.crew} testId={`peer-crew-${d.id}`} />
      <div className="text-[7px] uppercase tracking-wide text-slate-400 mt-1">{t("Read-only · teammate's schedule")}</div>
    </div>
  );
}
